<template>
  <form @submit.prevent="checkFormValidation" class="eso-form">
    <!-- Vesting Details Section -->
    <div class="form-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M10 18.3333C14.6024 18.3333 18.3333 14.6024 18.3333 10C18.3333 5.39763 14.6024 1.66667 10 1.66667C5.39763 1.66667 1.66667 5.39763 1.66667 10C1.66667 14.6024 5.39763 18.3333 10 18.3333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M10 5V10L13.3333 11.6667" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="card-title-group">
          <h4 class="card-title">Vesting Details</h4>
          <p class="card-subtitle">Configure vesting schedule and stock allocation</p>
        </div>
      </div>

      <div class="form-grid">
        <div class="form-group">
          <InputField
            :validate="validateForm"
            label="Number of shares"
            type="text"
            name="allocated_shares"
            placeholder="e.g., 10,000"
            :maxValue="totalAllocated"
            :currentValue="allocated_shares"
            :validateAsNumber="true"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
          <p class="shares-remaining" :class="{ 'shares-remaining--low': remainingShares < 500 }">
            <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M7 12.8333C10.2217 12.8333 12.8333 10.2217 12.8333 7C12.8333 3.77834 10.2217 1.16667 7 1.16667C3.77834 1.16667 1.16667 3.77834 1.16667 7C1.16667 10.2217 3.77834 12.8333 7 12.8333Z" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7 9.33333V7" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M7 4.66667H7.00583" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            Shares left: {{ convertToLocale(remainingShares) }}
          </p>
        </div>
        <div class="form-group">
          <ComboField class="combo-wrapper" currentValue="Months">
            <InputField
              class="combo-input"
              :validate="validateForm"
              label="Exercise timeline"
              :disabled="useDefault"
              type="number"
              :validateAsNumber="true"
              :currentValue="employee.exercise_timeline"
              name="exercise_timeline"
              placeholder="e.g., 24"
              @input-changed="storeValue"
              @is-valid="handleValidation"
            />
          </ComboField>
        </div>
        <div class="form-group">
          <ComboField class="combo-wrapper" currentValue="Months">
            <InputField
              class="combo-input"
              :validate="validateForm"
              label="Cliff period"
              type="number"
              :disabled="useDefault"
              :validateAsNumber="true"
              :currentValue="employee.cliff_period"
              name="cliff_period"
              placeholder="e.g., 12"
              @input-changed="storeValue"
              @is-valid="handleValidation"
            />
          </ComboField>
        </div>
        <div class="form-group">
          <ComboField class="combo-wrapper" currentValue="Years">
            <InputField
              class="combo-input"
              :validate="validateForm"
              label="Vesting Period"
              type="text"
              :validateAsNumber="true"
              :disabled="useDefault"
              :currentValue="employee.vesting_duration"
              name="vesting_duration"
              placeholder="e.g., 4"
              @input-changed="storeValue"
              @is-valid="handleValidation"
            />
          </ComboField>
        </div>
        <div class="form-group">
          <Dropdown
            name="vesting_frequency"
            :validate="validateForm"
            label="Vesting frequency"
            :currentValue="employee.vesting_frequency"
            :disabled="useDefault"
            :options="vestingOptions"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </div>
        <div class="form-group" v-if="marketPrice || $route.query.id">
          <InputField
            :validate="validateForm"
            label="Strike price (USD)"
            type="text"
            :validateAsNumber="true"
            :disabled="useDefault"
            :currentValue="strike_price"
            :maxValue="marketPrice"
            name="strike_price"
            placeholder="e.g., 1.50"
            @input-changed="storeValue"
            @is-valid="handleValidation"
          />
        </div>
        <div class="form-group">
          <label class="date-label">Start date</label>
          <Datepicker
            class="date-picker"
            :enableTimePicker="false"
            placeholder="Select start date"
            v-model="start_date"
            :inputClassName="isError"
          ></Datepicker>
          <p class="error-message" v-if="!start_date && isValidated">
            Start date is required
          </p>
        </div>
      </div>
    </div>

    <!-- Documents Section -->
    <div class="form-card">
      <div class="card-header">
        <div class="card-icon">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.5 8.33333V12.5C17.5 16.6667 15.8333 18.3333 11.6667 18.3333H8.33333C4.16667 18.3333 2.5 16.6667 2.5 12.5V7.5C2.5 3.33333 4.16667 1.66667 8.33333 1.66667H12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M17.5 8.33333H14.1667C11.6667 8.33333 10.8333 7.5 10.8333 5V1.66667L17.5 8.33333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </div>
        <div class="card-title-group">
          <h4 class="card-title">Legal Documents</h4>
          <p class="card-subtitle">Upload stock options legal documents (PDF)</p>
        </div>
      </div>

      <div class="documents-container">
        <div v-for="(document, index) in employee.documents" :key="index" class="document-item">
          <InputField
            class="document-name-input"
            :validate="validateForm"
            label="Document name"
            type="text"
            name="name"
            placeholder="e.g., Stock Option Agreement"
            :currentValue="document.name"
            @input-changed="setDocumentName($event, index)"
            @is-valid="handleDocumentValidation"
          />
          <DragNDrop
            accept=".pdf"
            :currentValue="document.url"
            :startUpload="uploadDocument"
            :documentName="document.name"
            :validate="documentValid"
            :hasMaxWidth="false"
            @re-upload="uploadDocument = false"
            @file-added="setDocument($event, index)"
            @upload-complete="setDocumentUrl($event, index)"
            @upload-error="uploadDocumentError"
            @remove-file="removeFile(document, index)"
            @is-valid="handleDocumentValidation"
          >
          </DragNDrop>
          <button
            v-if="employee.documents.length > 1"
            type="button"
            class="remove-document-btn"
            @click="removeDocument(document, index)"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14 3.98667C11.78 3.76667 9.54667 3.65333 7.32 3.65333C6 3.65333 4.68 3.72 3.36 3.85333L2 3.98667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M5.66667 3.31333L5.81333 2.44C5.92 1.80667 6 1.33333 7.12667 1.33333H8.87333C10 1.33333 10.0867 1.83333 10.1867 2.44667L10.3333 3.31333" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M12.5667 6.09333L12.1333 12.8067C12.06 13.8533 12 14.6667 10.14 14.6667H5.86C4 14.6667 3.94 13.8533 3.86667 12.8067L3.43333 6.09333" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6.88667 11H9.10667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
              <path d="M6.33333 8.33333H9.66667" stroke="currentColor" stroke-width="1.2" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
            <span>Remove</span>
          </button>
        </div>

        <button
          v-if="useDefault === false"
          type="button"
          class="add-document-btn"
          @click="addDocument"
        >
          <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6V12" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12 9H6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 16.5C13.1421 16.5 16.5 13.1421 16.5 9C16.5 4.85786 13.1421 1.5 9 1.5C4.85786 1.5 1.5 4.85786 1.5 9C1.5 13.1421 4.85786 16.5 9 16.5Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          <span>Add more documents</span>
        </button>
      </div>
    </div>

    <!-- Default Schedule Checkbox & Submit -->
    <div class="form-footer">
      <div class="checkbox-wrapper" v-if="!$route.query.id">
        <Checkbox
          title="Use default vesting schedule"
          label="Use default vesting schedule"
          value="secondaries"
          @checked="setDefault"
          :checked="useDefault"
        />
      </div>
      
      <button type="submit" class="submit-btn" :disabled="documentInfoLoading">
        <span v-if="!documentInfoLoading">Continue</span>
        <span v-else class="loading-state">
          <svg class="spinner" width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 1.5V4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M9 13.5V16.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
            <path d="M3.69727 3.69751L5.81977 5.82001" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.1802 12.18L14.3027 14.3025" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
            <path d="M1.5 9H4.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M13.5 9H16.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
            <path d="M3.69727 14.3025L5.81977 12.18" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            <path d="M12.1802 5.82001L14.3027 3.69751" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4"/>
          </svg>
          Processing...
        </span>
      </button>
    </div>
  </form>
</template>

<script>
import InputField from "@/components/general/InputField";
import CustomButton from "@/components/general/BtnComponent";
import ComboField from "@/components/general/ComboField";
import Checkbox from "@/components/general/Checkbox";
import DragNDrop from "@/components/general/DragNDrop";
import Dropdown from "@/components/general/Dropdown";
import { mapGetters, mapActions } from "vuex";
import { convertToLocale } from "@/utils/helpers.js";

export default {
  name: "EsoForm",
  
  data() {
    return {
      uploadDocument: false,
      documentInfoLoading: false,
      documentValid: false,
      showDefaultDocument: false,
      currentToken: {},
      employee: {
        vesting_duration: "",
        vesting_frequency: "",
        allocated_shares: "",
        cliff_period: "",
        strike_price: "",
        start_date: "",
        exercise_timeline: "",
        documents: [{ type: "Esop", name: "", url: "" }],
      },
      start_date: "",
      allocated_shares: "",
      strike_price: "",
      validationResult: [],
      documentValidation: [],
      uploaded: [],
      validateForm: false,
      vestingOptions: [
        "Monthly",
        "Yearly",
        "Quarterly",
        "Daily",
        "Bi-annually",
      ],
      useDefault: false,
      isValidated: false,
    };
  },
  
  emits: ["submit", "validate"],
  
  components: {
    InputField,
    CustomButton,
    ComboField,
    Dropdown,
    DragNDrop,
    Checkbox,
  },
  
  watch: {
    start_date(val) {
      this.employee.start_date = new Date(val).toISOString();
    },
    useDefault(val) {
      this.showDefaultDocument = false;
      if (val) {
        this.employee.documents = this.vestingSchedule.documents.slice("");
        if (this.employee.documents) {
          this.showDefaultDocument = true;
        }
        this.employee.vesting_duration = String(
          this.vestingSchedule.vesting_period
        );
        this.employee.vesting_frequency = this.frequency(
          this.vestingSchedule.vesting_frequency
        );
        this.employee.cliff_period = this.vestingSchedule.cliff_period;
        this.employee.exercise_timeline =
          this.vestingSchedule.exercise_timeline;
        this.strike_price = String(this.vestingSchedule.strike_price);
      }
    },
    validationResult: {
      deep: true,
      handler(val) {
        if (
          val.length === Object.keys(this.employee).length &&
          !val.includes(false)
        ) {
          this.documentInfoLoading = true;
          this.uploadDocument = true;
        }
      },
    },
    strikePriceFormatted(val) {
      this.strike_price = val;
    },
    sharesFormatted(val) {
      this.allocated_shares = val;
    },
  },
  
  computed: {
    ...mapGetters("Esop", ["vestingSchedule", "esopToken", "employeeInfo"]),
    ...mapGetters("organisationModule", ["organisationDetails"]),
    remainingShares() {
      const supply = this.esopToken?.supply;
      const totalRaised = this.esopToken?.total_allocated;
      const currentEmployeeShares = Number(this.employee.allocated_shares);
      const remainingShares = supply - (totalRaised + currentEmployeeShares);
      return remainingShares;
    },
    isError() {
      if (!this.start_date && this.isValidated) {
        return "date-input date-input--error";
      }
      return "date-input";
    },
    totalAllocated() {
      const supply = this.esopToken?.supply;
      const totalRaised = this.esopToken?.total_allocated;
      const totalAllocated = supply - totalRaised;
      return this.convertToLocale(totalAllocated);
    },
    strikePriceFormatted() {
      let strikePriceFormatted = null;
      if (this.strike_price) {
        const strike_price = Number(this.strike_price.split(",").join(""));
        this.employee.strike_price = strike_price;
        strikePriceFormatted = this.convertToLocale(strike_price);
      }
      return strikePriceFormatted;
    },
    sharesFormatted() {
      let sharesFormatted = null;
      if (this.allocated_shares) {
        const allocated_shares = Number(
          this.allocated_shares.split(",").join("")
        );
        this.employee.allocated_shares = allocated_shares;
        sharesFormatted = this.convertToLocale(allocated_shares);
      }
      return sharesFormatted;
    },
    marketPrice() {
      return (
        Number(this.esopToken?.valuation) /
        Number(this.esopToken?.total_supply)
      );
    },
  },
  
  mounted() {
    if (this.$route.query.id) {
      this.fillForm();
    }
  },
  
  methods: {
    ...mapActions("Esop", ["getEmployeeInvite"]),
    ...mapActions("assetModule", ["deleteDocument"]),
    convertToLocale,
    
    setDefault(val) {
      this.useDefault = val.value;
    },
    
    setDocument(url, index) {
      this.employee.documents[index].url = url;
    },
    
    async fillForm() {
      await this.getEmployeeInvite(this.$route.query.id);
      this.employee.vesting_duration = String(
        this.employeeInfo?.vesting_duration
      );
      this.employee.vesting_frequency = this.frequency(
        Number(this.employeeInfo?.vesting_frequency)
      );
      this.allocated_shares = String(this.employeeInfo?.allocated_shares);
      this.employee.cliff_period = this.employeeInfo?.cliff_period;
      this.strike_price = String(this.employeeInfo?.strike_price);
      this.employee.exercise_timeline = this.employeeInfo?.exercise_timeline;
      this.employee.documents = this.employeeInfo?.documents.slice("");
      this.start_date = this.employeeInfo?.createdAt;
    },
    
    frequency(value) {
      let frequency = null;
      switch (value) {
        case "Daily":
          frequency = 1;
          break;
        case 1:
          frequency = "Daily";
          break;
        case 30:
          frequency = "Monthly";
          break;
        case "Monthly":
          frequency = 30;
          break;
        case 91:
          frequency = "Quarterly";
          break;
        case "Quarterly":
          frequency = 91;
          break;
        case 182:
          frequency = "Bi-annually";
          break;
        case "Bi-annually":
          frequency = 182;
          break;
        case 365:
          frequency = "Yearly";
          break;
        case "Yearly":
          frequency = 365;
          break;
      }
      return frequency;
    },
    
    storeValue(object) {
      if (
        object.name !== "strike_price" &&
        object.name !== "allocated_shares"
      ) {
        this.employee[object.name] = object.value;
      } else {
        this[object.name] = object.value;
      }
    },
    
    checkFormValidation() {
      this.validationResult = [];
      this.uploaded = [];
      this.documentValidation = [];
      this.validateForm = true;
      this.isValidated = true;
      this.documentValid = true;
      if (this.start_date) {
        this.handleValidation(true);
      } else {
        this.handleValidation(false);
      }
      setTimeout(() => {
        this.validateForm = false;
        this.documentValid = false;
        this.isValidated = false;
      }, 1000);
    },
    
    handleValidation(value) {
      this.validationResult.push(value);
    },
    
    handleDocumentValidation(value) {
      this.documentValidation.push(value);
      if (
        this.documentValidation.length / 2 ===
        this.employee.documents.length
      ) {
        if (this.documentValidation.includes(false)) {
          this.handleValidation(false);
        } else {
          this.handleValidation(true);
        }
      }
    },
    
    sendInviteInfo() {
      const payload = {
        vesting_duration: String(this.employee.vesting_duration),
        vesting_frequency: this.frequency(this.employee.vesting_frequency),
        allocated_shares: String(this.employee.allocated_shares),
        cliff_period: String(this.employee.cliff_period),
        strike_price: String(this.employee.strike_price),
        exercise_timeline: String(this.employee.exercise_timeline),
        start_date: this.employee.start_date,
        employee_type: "ESOP_EMPLOYEE",
        documents: this.employee.documents,
      };

      this.$emit("validate", this.validationResult);
      this.$emit("submit", {
        payload,
      });
      this.documentInfoLoading = false;
      this.uploadDocument = false;
    },

    uploadDocumentError() {
      this.documentInfoLoading = false;
      this.uploadTokenImage = false;
    },
    
    setDocumentName(val, index) {
      this.employee.documents[index].name = val.value;
    },
    
    addDocument() {
      this.employee.documents.push({ type: "Esop", name: "", url: "" });
    },
    
    removeFile(document, index) {
      this.employee.documents[index] = {
        type: "Esop",
        name: document.name,
        url: "",
      };
      this.useDefault = false;
    },
    
    removeDocument(document, index) {
      this.employee.documents.splice(index, 1);
      this.useDefault = false;
      const inviteId = this.$route.query.id;
      if (inviteId && document.url.includes("cloudinary")) {
        const assetId = this.esopToken.asset;
        const payload = {
          docId: document._id,
          assetId,
          type: "Esop",
        };
        this.deleteDocument(payload);
      }
    },
    
    setDocumentUrl(url, index) {
      this.employee.documents[index].url = url;
      this.uploaded.push(true);
      if (this.uploaded.length === this.employee.documents.length) {
        this.sendInviteInfo();
        this.documentInfoLoading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.eso-form {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.form-card {
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
  padding: 24px;
}

.card-header {
  display: flex;
  align-items: flex-start;
  gap: 14px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 1px solid #E2E8F0;
}

.card-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  background: white;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  color: #4D84FF;
  flex-shrink: 0;
}

.card-title-group {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.card-title {
  font-family: 'DM Sans', sans-serif;
  font-size: 15px;
  font-weight: 500;
  color: #0F172A;
  margin: 0;
}

.card-subtitle {
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  color: #64748B;
  margin: 0;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
}

.form-group {
  display: flex;
  flex-direction: column;
}

.shares-remaining {
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  margin-top: 6px;
  text-transform: uppercase;
  letter-spacing: 0.3px;

  &--low {
    color: #DC2626;
  }
}

.combo-wrapper {
  width: 100%;
}

.combo-input {
  flex-grow: 1;
}

.date-label {
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  color: #0F172A;
  margin-bottom: 8px;
  display: block;
}

.date-picker {
  width: 100%;
}

.error-message {
  font-family: 'DM Sans', sans-serif;
  font-size: 12px;
  color: #DC2626;
  margin-top: 6px;
}

.documents-container {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.document-item {
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  border-bottom: 1px solid #E2E8F0;

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
}

.document-name-input {
  margin-bottom: 0;
}

.remove-document-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: transparent;
  border: 1px solid #FCA5A5;
  border-radius: 8px;
  color: #DC2626;
  font-family: 'DM Sans', sans-serif;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-end;

  &:hover {
    background: #FEE2E2;
    border-color: #F87171;
  }
}

.add-document-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px dashed #CBD5E1;
  border-radius: 8px;
  color: #4D84FF;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;

  &:hover {
    background: #EEF2FF;
    border-color: #4D84FF;
    border-style: solid;
  }
}

.form-footer {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.checkbox-wrapper {
  padding: 16px 20px;
  background: #F8FAFC;
  border: 1px solid #E2E8F0;
  border-radius: 10px;
}

.submit-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 14px 32px;
  background: #4D84FF;
  border: none;
  border-radius: 8px;
  color: white;
  font-family: 'DM Sans', sans-serif;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  align-self: flex-start;
  min-width: 140px;

  &:hover:not(:disabled) {
    background: #3B6FE8;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.loading-state {
  display: flex;
  align-items: center;
  gap: 8px;
}

.spinner {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>

<style lang="scss">
.date-input {
  height: 48px;
  flex-grow: 1;
  border: 1px solid #E2E8F0;
  border-radius: 8px;
  font-family: 'DM Sans', sans-serif;
  padding: 0 14px;
  
  &--error {
    border: 1px solid #DC2626;
  }
  
  &:focus {
    outline: none;
    border-color: #4D84FF;
  }
}

.dp__main {
  width: 100%;
}
</style>
